import { ComponentType, Overlay, OverlayRef } from "@angular/cdk/overlay";
import { ComponentPortal } from "@angular/cdk/portal";
import { EventEmitter, Injectable, OnDestroy, Output } from "@angular/core";
import { NavigationStart, Router } from "@angular/router";
import { fromEvent, Subscription } from "rxjs";
import { filter } from "rxjs/operators";

import { BaseModalComponent } from "../..";

@Injectable({ providedIn: "root" })
export class HaModalService implements OnDestroy {
    private overlayRefs!: OverlayRef[];
    private readonly focusRestoreElements = new Map<OverlayRef, HTMLElement>();

    private routerSubscription: Subscription;

    constructor(private overlay: Overlay, private router: Router) {
        this.routerSubscription = this.router.events.pipe(
            filter((e) => e instanceof NavigationStart),
            filter(() => !!this.overlayRefs),
        ).subscribe(() => {
            this.removeAll();
        });
    }

    @Output()
    public onClose: EventEmitter<void> = new EventEmitter<void>();

    public ngOnDestroy(): void {
        this.routerSubscription.unsubscribe();
        this.removeAll();
    }

    public open<T extends BaseModalComponent>(
        component: ComponentType<T>,
        closeOnEscapeOrOutsideClick: boolean,
        componentData: unknown = undefined,
        showPanels: "none" | "header" | "both" = "none",
    ): void {
        const positionStrategy = this.overlay.position().global();
        // .centerHorizontally()
        // .centerVertically();

        // We create the overlay
        this.overlayRefs = this.overlayRefs ?? [];

        var backdropClass = ["ha-modal-backdrop"];
        var panelClass = ["ha-modal"];

        if (showPanels === "both") {
            backdropClass = [...backdropClass, "ha-modal-backdrop--panels"];
            panelClass = [...panelClass, "ha-modal--panels"];
        } else if (showPanels === "header") {
            backdropClass = [...backdropClass, "ha-modal-backdrop--header"];
            panelClass = [...panelClass, "ha-modal--header"];
        }

        const newOverlay = this.overlay.create({
            backdropClass: backdropClass,
            disposeOnNavigation: true,
            hasBackdrop: true,
            panelClass: panelClass,
            positionStrategy: positionStrategy,
        });

        this.overlayRefs.push(newOverlay);

        // If modal doesn't require an active choice, close
        // on backdrop click and escape keypress.
        if (closeOnEscapeOrOutsideClick) {
            newOverlay.backdropClick().subscribe(() => {
                this.removeLatest();
            });

            fromEvent<KeyboardEvent>(document, "keydown").subscribe((event: KeyboardEvent) => {
                if (event.key === "Escape") {
                    this.removeLatest();
                }
            });
        }

        // Then we create a portal to render a component
        const componentPortal = new ComponentPortal(component);

        // We render the portal in the overlay
        const componentRef = newOverlay.attach(componentPortal);
        componentRef.instance.data = componentData;
        this.configureAccessibility(newOverlay);

        // Remove overlay on close modal event from component
        componentRef.instance.closeModalEvent.subscribe(() => {
            this.removeLatest();
        });
    }

    private removeLatest() {
        const overlay = this.overlayRefs.splice(-1)[0];
        overlay.detach();
        this.focusRestoreElements.get(overlay)?.focus();
        this.focusRestoreElements.delete(overlay);

        this.onClose.emit();
    }

    private removeAll() {
        this.overlayRefs.forEach((_) => {
            _.dispose();
            this.focusRestoreElements.get(_)?.focus();
            this.focusRestoreElements.delete(_);
        });

        this.overlayRefs.length = 0;
    }

    private configureAccessibility(overlay: OverlayRef): void {
        const overlayElement = overlay.overlayElement;
        const activeElement = document.activeElement;

        if (activeElement instanceof HTMLElement) {
            this.focusRestoreElements.set(overlay, activeElement);
        }

        overlayElement.setAttribute("role", "dialog");
        overlayElement.setAttribute("aria-modal", "true");

        const heading = overlayElement.querySelector("h1, h2, h3, h4, h5, h6");
        if (heading instanceof HTMLElement) {
            const headingId = heading.id || `ha-modal-title-${this.overlayRefs.length}`;
            heading.id = headingId;
            overlayElement.setAttribute("aria-labelledby", headingId);
        }

        const focusTarget = overlayElement.querySelector<HTMLElement>(
            "button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])",
        );

        if (focusTarget) {
            focusTarget.focus();
        } else {
            overlayElement.tabIndex = -1;
            overlayElement.focus();
        }
    }
}
